import {
  CheckboxGroup,
  CheckboxOption,
  ErrorText,
  FieldContainer,
  RadioGroup,
  RadioOption,
} from './StyledComponents'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  formatPhoneNumber,
  parsePhoneNumber,
} from '../../utils/EnrollmentApplicationFormUtils'

import Button from '../common/Button/Button.component'
import CloseIconButton from '../common/Button/CloseIconButton.component'
import { DateValidationFunction } from '../common/DateField'
import { FormFieldDto } from '../../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import InputField from '../common/InputField/InputField.component'
import Label from '../common/Label/Label'
import MuiYearMonthDateField from '../common/YearMonthField'
import SectionRow from '../common/SectionRow'
import Select from '../common/Select/Select.component'
import YearMonthDayField from '../common/YearMonthDayField'
import { fetchDetailsFromCustomEndpoint } from '../../service/quotes'
import theme from '../../global/theme'

export const LinkField = ({ field }: { field: FormFieldDto }) => (
  <FieldContainer>
    <a
      href={field.url || '#'}
      target='_blank'
      rel='noopener noreferrer'
      style={{
        color: theme.colors.primary,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: '14px',
      }}
    >
      {field.display_label}
    </a>
  </FieldContainer>
)

export const TextField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <InputField
      label={field.display_label}
      isRequired={isRequired}
      type='text'
      value={value || ''}
      onChange={(e) => {
        if (e.target.value.length > (field.maxlength || 200)) {
          return
        }
        onChange(e.target.value)
      }}
      placeholder={field.placeholder}
      error={!!error}
      width='100%'
      helperText={error}
    />
  </FieldContainer>
)

export const TextAreaField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Label required={isRequired}>{field.display_label}</Label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      maxLength={field.maxlength}
      placeholder={field.placeholder}
      style={{
        width: '100%',
        padding: '12px',
        border: `1px solid ${
          error ? theme.colors.secondary : theme.colors.borderColor
        }`,
        borderRadius: '16px',
        fontSize: '14px',
        minHeight: '100px',
        resize: 'vertical',
        marginTop: '10px',
      }}
    />
    {error && <ErrorText>{error}</ErrorText>}
  </FieldContainer>
)

export const RadioField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Label required={isRequired}>{field.display_label}</Label>
    <RadioGroup>
      {field.options?.map((option) => (
        <RadioOption key={option.value}>
          <input
            type='radio'
            name={field.id}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            style={{ marginRight: '8px' }}
          />
          <span>{option.key}</span>
        </RadioOption>
      ))}
    </RadioGroup>
    {error && <ErrorText>{error}</ErrorText>}
  </FieldContainer>
)

export const CheckboxField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: (string | number)[]
  onChange: (val: (string | number)[]) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Label required={isRequired}>{field.display_label}</Label>
    <CheckboxGroup>
      {field.options?.map((option) => (
        <CheckboxOption key={option.id || option.value}>
          <input
            type='checkbox'
            checked={(value || []).includes(option.id || option.value)}
            onChange={(e) => {
              const currentValues = value || []
              const optionId = option.id || option.value
              if (e.target.checked) {
                onChange([...currentValues, optionId])
              } else {
                onChange(currentValues.filter((v) => v !== optionId))
              }
            }}
            style={{ marginRight: '8px' }}
          />
          <span>{option.key}</span>
        </CheckboxOption>
      ))}
    </CheckboxGroup>
    {error && <ErrorText>{error}</ErrorText>}
  </FieldContainer>
)

export const SelectField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Select
      isRequired={isRequired}
      label={field.display_label}
      value={
        field.type === 'int_picker_field'
          ? value
          : field.options?.find((option) => value === option.value)?.key || ''
      }
      onChange={(e) => {
        let selectedValue
        if (field.type === 'int_picker_field') {
          selectedValue = e.target.value
        } else {
          selectedValue = field.options?.find(
            (option) => option.key === e.target.value
          )?.value
        }

        onChange(selectedValue)
      }}
      error={!!error}
      options={
        field.options?.map((option) => {
          if (field.type === 'int_picker_field' && typeof option === 'number') {
            return option
          }
          return option.key
        }) || []
      }
      helperText={error}
      bottomPadding={false}
    />
  </FieldContainer>
)

interface PhoneFieldProps {
  field: FormFieldDto
  value: {
    area_code: string
    central_office_code: string
    station_code: string
  }
  onChange: (val: {
    area_code: string
    central_office_code: string
    station_code: string
  }) => void
  error?: string
  isRequired?: boolean
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}) => {
  const [displayValue, setDisplayValue] = useState('')

  // Initialize display value from props
  useEffect(() => {
    if (value?.area_code || value?.central_office_code || value?.station_code) {
      const fullNumber =
        (value.area_code || '') +
        (value.central_office_code || '') +
        (value.station_code || '')
      setDisplayValue(formatPhoneNumber(fullNumber))
    }
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const numbers = inputValue.replace(/\D/g, '')

    // Limit to 10 digits max
    if (numbers.length > 10) return

    // Format and set display value
    const formatted = formatPhoneNumber(numbers)
    setDisplayValue(formatted)

    // Parse and update the structured value
    const parsedValue = parsePhoneNumber(formatted)
    onChange(parsedValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right, down, up
      (e.keyCode >= 35 && e.keyCode <= 40)
    ) {
      return
    }

    // Ensure that it is a number and stop the keypress
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault()
    }
  }

  const handleBlur = () => {
    // Clean up formatting on blur if incomplete
    const numbers = displayValue.replace(/\D/g, '')
    if (numbers.length > 0 && numbers.length < 10) {
      // Keep partial formatting for better UX
      setDisplayValue(formatPhoneNumber(numbers))
    }
  }

  return (
    <FieldContainer isError={!!error}>
      <InputField
        isRequired={true}
        label={field.display_label}
        width='100%'
        type='tel'
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder='(123) 456-7890'
        error={!!error}
        helperText={error}
      />
    </FieldContainer>
  )
}

export const DateField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
  customValidation,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
  customValidation?: DateValidationFunction
}) => (
  <FieldContainer isError={!!error}>
    <YearMonthDayField
      bottomPadding={false}
      label={field.display_label}
      isRequired={isRequired}
      value={value || ''}
      onChange={onChange}
      error={!!error}
      errorText={error}
      customValidation={customValidation}
    />
  </FieldContainer>
)

export const YearMonthDateField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
  customValidation,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
  customValidation?: DateValidationFunction
}) => (
  <FieldContainer isError={!!error}>
    <MuiYearMonthDateField
      bottomPadding={false}
      label={field.display_label}
      isRequired={isRequired}
      value={value || ''}
      onChange={onChange}
      error={!!error}
      errorText={error}
      customValidation={customValidation}
    />
  </FieldContainer>
)

export const SSNField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <InputField
      width='100%'
      label={field.display_label}
      isRequired={isRequired}
      type='text'
      value={value || ''}
      onChange={(e) => {
        // Format SSN as user types
        let val = e.target.value.replace(/\D/g, '')

        if (val.length > 9) {
          return
        }
        if (val.length >= 6) {
          val = val.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')
        } else if (val.length >= 4) {
          val = val.replace(/(\d{3})(\d{2})/, '$1-$2')
        }
        onChange(val)
      }}
      placeholder={field.placeholder}
      error={!!error}
      helperText={error}
    />
  </FieldContainer>
)

export const HeightField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: {
    feet: number
    inches: number
  }
  onChange: (val: { feet: number; inches: number }) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Label required={isRequired}>{field.display_label}</Label>
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '5px',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <InputField
          width='100%'
          type='number'
          placeholder='Feet'
          value={value?.feet !== undefined ? value.feet.toString() : ''}
          endSymbol={
            <span
              style={{
                color: theme.colors.primary,
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              ft
            </span>
          }
          onChange={(e) => {
            let feet = e.target.value === '' ? 0 : parseInt(e.target.value) || 0
            onChange({
              ...value,
              feet,
            })
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: '5px',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <InputField
          endSymbol={
            <span
              style={{
                color: theme.colors.primary,
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              in
            </span>
          }
          width='100%'
          type='number'
          placeholder='Inches'
          value={value?.inches !== undefined ? value.inches.toString() : ''}
          onChange={(e) => {
            let inches =
              e.target.value === '' ? 0 : parseInt(e.target.value) || 0
            onChange({
              ...value,
              inches,
            })
          }}
        />
      </div>
    </div>
    {error && <ErrorText>{error}</ErrorText>}
  </FieldContainer>
)

export const WeightField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: number
  onChange: (val: number) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <InputField
      endSymbol={
        <span
          style={{
            color: theme.colors.primary,
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          lbs
        </span>
      }
      width='100%'
      isRequired={isRequired}
      label={field.display_label}
      type='number'
      value={value || ''}
      onChange={(e) => {
        onChange(parseInt(e.target.value) || 0)
      }}
      onBlur={() => {
        if (value < (field.minimum_value || 0)) value = field.minimum_value || 0
        if (value > (field.maximum_value || 0)) value = field.maximum_value || 0
        onChange(value)
      }}
      placeholder='Weight in pounds'
      error={!!error}
      helperText={error}
    />
  </FieldContainer>
)

export const EmailField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <InputField
      width='100%'
      label={field.display_label}
      isRequired={isRequired}
      type='email'
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder='Enter email address'
      error={!!error}
      helperText={error}
    />
  </FieldContainer>
)

export const HeadingField = ({ field }: { field: FormFieldDto }) => {
  const HeadingTag = field.heading_type || 'h3'
  return (
    <div style={{ margin: '20px 0' }}>
      <HeadingTag
        style={{
          margin: '0 0 10px 0',
          fontSize: field.heading_type === 'h5' ? '16px' : undefined,
          fontWeight: field.heading_type === 'label' ? 'normal' : 'bold',
          color: theme.colors.secondary,
        }}
      >
        {field.display_label}
      </HeadingTag>
    </div>
  )
}

export const TextBlockField = ({ field }: { field: FormFieldDto }) => (
  <div
    className={field.classes?.join(' ')}
    style={{
      padding: '15px',
      margin: '10px 0',
      borderRadius: '16px',
      backgroundColor: field.classes?.includes('alert-danger')
        ? theme.colors.contrastSecondary
        : theme.colors.highlight,
      border: field.classes?.includes('alert-danger')
        ? `1px solid ${theme.colors.secondary}`
        : `1px solid ${theme.colors.warning}`,
      fontSize: '14px',
    }}
  >
    {field.display_label}
  </div>
)

interface SearchOption {
  label: string
  data: Record<string, any>
}

interface SearchFieldProps {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
  formData: Record<string, any>
  updateField: (id: string, value: any) => void
}

export const SearchField: React.FC<SearchFieldProps> = ({
  field,
  value,
  onChange,
  error,
  isRequired,
  formData,
  updateField,
}) => {
  const [allOptions, setAllOptions] = useState<SearchOption[]>([]) // Store all API results
  const [filteredOptions, setFilteredOptions] = useState<SearchOption[]>([]) // Filtered and sorted options
  const [isLoading, setIsLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(
    null
  )
  const [searchError, setSearchError] = useState<string | null>(null)
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('') // Track last API call

  // Filter and sort options based on current input
  const filterAndSortOptions = useCallback(
    (options: SearchOption[], searchTerm: string) => {
      if (!searchTerm.trim()) return options

      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )

      // Sort lexically by label
      return filtered.sort((a, b) => a.label.localeCompare(b.label))
    },
    []
  )

  // Debounced search function
  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (
        !searchTerm ||
        !searchTerm.trim() ||
        searchTerm.length < 3 ||
        !field.path
      ) {
        setAllOptions([])
        setFilteredOptions([])
        setShowDropdown(false)
        setSelectedOption(null)
        clearChildFields()
        return
      }

      // Don't make API call if we already have data for this search term
      if (lastSearchTerm === searchTerm && allOptions.length > 0) {
        const filtered = filterAndSortOptions(allOptions, searchTerm)
        setFilteredOptions(filtered)
        handleFilteredResults(filtered)
        return
      }

      // Don't retry if there's already an error for this search term
      if (searchError && lastSearchTerm === searchTerm) {
        return
      }

      setIsLoading(true)
      setSearchError(null)

      try {
        const { data: response } = await fetchDetailsFromCustomEndpoint(
          field.path,
          searchTerm
        )

        if (Array.isArray(response) && response.length > 0) {
          const options: SearchOption[] = response.map((item, index) => ({
            label:
              (item && item[field.search_data_column || 'name']) ||
              `Option ${index + 1}`,
            data: item || {},
          }))

          setAllOptions(options)
          setLastSearchTerm(searchTerm)

          // Filter and sort the results
          const filtered = filterAndSortOptions(options, searchTerm)
          setFilteredOptions(filtered)
          handleFilteredResults(filtered)
        } else {
          setAllOptions([])
          setFilteredOptions([])
          setShowDropdown(false)
          setSelectedOption(null)
          clearChildFields()
          setLastSearchTerm(searchTerm) // Remember this search term even if no results
        }
      } catch (err) {
        console.error('Search error:', err)
        setSearchError('Search failed. Please try again.')
        setLastSearchTerm(searchTerm) // Remember failed search term to prevent retries
        setAllOptions([])
        setFilteredOptions([])
        setShowDropdown(false)
        setSelectedOption(null)
        clearChildFields()
      } finally {
        setIsLoading(false)
      }
    },
    [
      field.path,
      field.search_data_column,
      lastSearchTerm,
      allOptions,
      searchError,
      filterAndSortOptions,
    ]
  )

  // Handle the filtered results (single vs multiple options)
  const handleFilteredResults = useCallback((filtered: SearchOption[]) => {
    if (filtered.length === 1) {
      // Auto-select single option
      const singleOption = filtered[0]
      setSelectedOption(singleOption)
      setShowDropdown(false)
      populateFields(singleOption.data)
    } else if (filtered.length > 1) {
      // Show dropdown for multiple options
      setShowDropdown(true)
      setSelectedOption(null)
      clearChildFields()
    } else {
      // No results
      setShowDropdown(false)
      setSelectedOption(null)
      clearChildFields()
    }
  }, [])

  // Debounce search calls
  useEffect(() => {
    // Prevent infinite loops on API failures
    if (searchError) return

    const timeoutId = setTimeout(() => {
      if (value !== null && value !== undefined && value.length >= 3) {
        // If user is typing after a selection, filter existing results first
        if (selectedOption && allOptions.length > 0) {
          const filtered = filterAndSortOptions(allOptions, value)
          setFilteredOptions(filtered)

          // Check if current input still matches selected option
          const stillMatches = filtered.some(
            (option) => option.label === selectedOption.label
          )
          if (!stillMatches) {
            setSelectedOption(null)
            clearChildFields()
          }

          // Only show dropdown if we have multiple filtered options
          setShowDropdown(filtered.length > 1)

          // Only make API call if no good local matches and input changed significantly
          if (
            filtered.length === 0 ||
            Math.abs(value.length - lastSearchTerm.length) > 2
          ) {
            performSearch(value)
          }
        } else {
          performSearch(value)
        }
      } else if (!value || (value && value.length < 3)) {
        // Clear everything if input is too short
        setAllOptions([])
        setFilteredOptions([])
        setShowDropdown(false)
        setSelectedOption(null)
        clearChildFields()
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [value]) // Removed other dependencies to prevent infinite loops

  const populateFields = (data: Record<string, any>) => {
    if (!data) return

    // Populate main field if it has search_data_column
    if (field.search_data_column && data[field.search_data_column]) {
      onChange(data[field.search_data_column])
    }

    // Populate child fields
    if (field.child_fields && Array.isArray(field.child_fields)) {
      field.child_fields.forEach((childField) => {
        if (
          childField?.search_data_column &&
          data[childField.search_data_column]
        ) {
          updateField(childField.id, data[childField.search_data_column])
        }
      })
    }
  }

  const clearChildFields = () => {
    if (field.child_fields && Array.isArray(field.child_fields)) {
      field.child_fields.forEach((childField) => {
        if (childField?.id) {
          updateField(childField.id, '')
        }
      })
    }
  }

  const handleOptionSelect = (option: SearchOption) => {
    setSelectedOption(option)
    setShowDropdown(false)
    populateFields(option.data)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value || ''
    onChange(newValue)

    // Reset selection if user types something different
    if (selectedOption && newValue !== selectedOption.label) {
      setSelectedOption(null)
    }

    // Clear search error when user types something new (different from last failed search)
    if (searchError && newValue !== lastSearchTerm) {
      setSearchError(null)
    }
  }

  const shouldShowChildFields =
    selectedOption ||
    (filteredOptions.length === 1 && value && value.length >= 3)

  return (
    <FieldContainer
      style={{ position: 'relative', marginBottom: '20px' }}
      isError={!!(error || searchError)}
    >
      <InputField
        label={field.display_label}
        isRequired={isRequired}
        width='100%'
        type='text'
        value={value || ''}
        onChange={(e) => {
          if (e.target.value.length > (field.maxlength || 200)) {
            return
          }
          handleInputChange(e)
        }}
        placeholder={field.default_text || 'Type to search...'}
        error={!!(error || searchError)}
        disabled={isLoading}
        helperText={error}
      />

      {isLoading && (
        <div
          style={{
            fontSize: '12px',
            color: theme.colors.textGrey,
          }}
        >
          Searching...
        </div>
      )}

      {/* Dropdown for multiple options */}
      {showDropdown && filteredOptions.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              onClick={() => handleOptionSelect(option)}
              style={{
                padding: '10px 15px',
                cursor: 'pointer',
                borderBottom:
                  index < filteredOptions.length - 1
                    ? '1px solid #f0f0f0'
                    : 'none',
                backgroundColor: 'white',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}

      {/* Child fields - only show when we have a selection */}
      {shouldShowChildFields &&
        field.child_fields &&
        field.child_fields.length > 0 && (
          <div
            style={{
              marginTop: '15px',
              padding: '20px',
              backgroundColor: theme.colors.backgroundColorContainer,
              borderRadius: '16px',
              border: `1px solid ${theme.colors.borderColor}`,
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: theme.colors.textGrey,
                marginBottom: '15px',
              }}
            >
              Auto-populated Information:
            </div>

            <SectionRow bottomPadding={false} row>
              {field.child_fields.map((childField) => (
                <div key={childField.id}>
                  <Label required={childField.required}>
                    {childField.display_label}
                  </Label>
                  <InputField
                    width='100%'
                    type='text'
                    value={formData[childField.id] || ''}
                    onChange={(e) => updateField(childField.id, e.target.value)}
                    disabled={true}
                  />
                </div>
              ))}
            </SectionRow>
          </div>
        )}

      {/* Error messages */}
      {searchError && <ErrorText>{searchError}</ErrorText>}

      {/* No results message */}
      {value &&
        value.length >= 3 &&
        !isLoading &&
        filteredOptions.length === 0 &&
        !searchError && (
          <div
            style={{
              fontSize: '12px',
              color: theme.colors.textGrey,
              marginTop: '5px',
              fontStyle: 'italic',
            }}
          >
            No results found for "{value}"
          </div>
        )}
    </FieldContainer>
  )
}

export const ComplexRadioField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: string
  onChange: (val: string) => void
  error?: string
  isRequired?: boolean
}) => (
  <FieldContainer isError={!!error}>
    <Label required={isRequired}>{field.display_label}</Label>
    <RadioGroup>
      {field.child_fields?.map((childField) => (
        <div
          key={childField.id}
          style={{
            marginBottom: '10px',
            padding: '20px',
            border: `1px solid ${theme.colors.borderColor}`,
            background: theme.colors.backgroundColorContainer,
            borderRadius: '16px',
          }}
        >
          {childField.display_label && (
            <div
              style={{
                fontWeight: '600',
                marginBottom: '10px',
                fontSize: '14px',
                color: theme.colors.textDark,
              }}
            >
              {childField.display_label}
            </div>
          )}
          {childField.options?.map((option) => (
            <RadioOption key={option.value}>
              <input
                type='radio'
                name={childField.group_name || childField.id}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                style={{ marginRight: '8px' }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: theme.colors.textGrey,
                  marginBottom: '5px',
                }}
              >
                {option.key}
              </span>
            </RadioOption>
          ))}
        </div>
      ))}
    </RadioGroup>
    {error && <ErrorText>{error}</ErrorText>}
  </FieldContainer>
)

export const AddressField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
  formData,
  updateField,
}: {
  field: FormFieldDto
  value: {
    address_line1?: string
    address_line2?: string
    zip5?: string
    address_city?: string
    address_state?: string
    county?: string
  }
  onChange: (val: {
    address_line1?: string
    address_line2?: string
    zip5?: string
    address_city?: string
    address_state?: string
    county?: string
  }) => void
  error?: string
  isRequired?: boolean
  formData?: any
  updateField?: (fieldId: string, value: any, sectionId?: any) => void
}) => {
  const [isLoadingZip, setIsLoadingZip] = useState(false)
  const [zipLookupFailed, setZipLookupFailed] = useState(false)
  const [lastLookupZip, setLastLookupZip] = useState<string>('')

  // Store current value in ref to avoid stale closures
  const currentValueRef = useRef(value)
  currentValueRef.current = value

  const performZipLookup = useCallback(
    async (zipCode: string) => {
      if (!zipCode || zipCode.length !== 5 || lastLookupZip === zipCode) {
        return
      }

      setIsLoadingZip(true)
      setZipLookupFailed(false)
      setLastLookupZip(zipCode)

      try {
        const { data: response } = await fetchDetailsFromCustomEndpoint(
          '/location_data?zip5=',
          zipCode
        )

        if (Array.isArray(response) && response.length > 0) {
          const locationData = response[0]

          // Auto-populate city, state, county using current value from ref
          onChange({
            ...currentValueRef.current,
            zip5: zipCode,
            address_city: locationData.city || '',
            address_state: locationData.state || '',
            county: locationData.county_name || '',
          })
          setZipLookupFailed(false)
        } else {
          setZipLookupFailed(true)
          onChange({
            ...currentValueRef.current,
            zip5: zipCode,
            address_city: '',
            address_state: '',
            county: '',
          })
        }
      } catch (err) {
        console.error('ZIP lookup error:', err)
        setZipLookupFailed(true)
        onChange({
          ...currentValueRef.current,
          zip5: zipCode,
          address_city: '',
          address_state: '',
          county: '',
        })
      } finally {
        setIsLoadingZip(false)
      }
    },
    [lastLookupZip, onChange]
  )

  // Determine if city, state, county fields should be shown (only after successful ZIP lookup)
  const shouldShowLocationFields =
    value?.zip5 &&
    value.zip5.length === 5 &&
    !zipLookupFailed &&
    !isLoadingZip &&
    (value.address_city || value.address_state || value.county)

  // Debounce ZIP lookup
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (value?.zip5 && value.zip5.length === 5) {
        performZipLookup(value.zip5)
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [value?.zip5]) // Remove performZipLookup from dependencies to prevent infinite loops

  return (
    <FieldContainer isError={!!error}>
      <Label required={isRequired}>{field.display_label}</Label>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginTop: '15px',
          padding: '20px',
          backgroundColor: theme.colors.backgroundColorContainer,
          borderRadius: '16px',
          border: !!error
            ? `1px solid ${theme.colors.secondary}`
            : `1px solid ${theme.colors.borderColor}`,
        }}
      >
        {/* Address Line 1 */}
        {field.address_line_1_field_id && (
          <div>
            <InputField
              width='100%'
              type='text'
              label='Address Line 1'
              isRequired={field.address_line_1_is_required === 'yes'}
              value={value?.address_line1 || ''}
              onChange={(e) => {
                onChange({
                  ...value,
                  address_line1: e.target.value,
                })
              }}
              placeholder='Address Line 1'
            />
          </div>
        )}

        {/* Address Line 2 */}
        {field.address_line_2_field_id && (
          <div>
            <InputField
              width='100%'
              type='text'
              label='Address Line 2'
              isRequired={field.address_line_2_is_required === 'yes'}
              value={value?.address_line2 || ''}
              onChange={(e) => {
                onChange({
                  ...value,
                  address_line2: e.target.value,
                })
              }}
              placeholder='Address Line 2'
            />
          </div>
        )}

        {/* ZIP Code */}
        {field.zip5_field_id && (
          <div>
            <InputField
              width='100%'
              type='text'
              label='ZIP Code'
              isRequired={field.zip5_is_required === 'yes'}
              value={value?.zip5 || ''}
              onChange={(e) => {
                const zipValue = e.target.value.replace(/\D/g, '').slice(0, 5)
                onChange({
                  ...value,
                  zip5: zipValue,
                })
              }}
              placeholder='12345'
            />
            {isLoadingZip && (
              <div
                style={{
                  fontSize: '12px',
                  color: theme.colors.textGrey,
                  marginTop: '5px',
                }}
              >
                Looking up ZIP code...
              </div>
            )}
          </div>
        )}

        {/* City, State, County - Only shown after successful ZIP lookup */}
        {shouldShowLocationFields && (
          <SectionRow row>
            {/* City */}
            {field.city_field_id && (
              <InputField
                width='100%'
                type='text'
                label='City'
                isRequired={field.city_is_required === 'yes'}
                value={value?.address_city || ''}
                onChange={(e) => {
                  // Fields are always disabled, but keeping onChange for consistency
                  const cityValue = field.city_maximum_length
                    ? e.target.value.slice(0, field.city_maximum_length)
                    : e.target.value
                  onChange({
                    ...value,
                    address_city: cityValue,
                  })
                }}
                placeholder='City'
                disabled={true}
              />
            )}

            {/* State */}
            {field.state_field_id && (
              <InputField
                width='100%'
                type='text'
                label='State'
                isRequired={field.state_is_required === 'yes'}
                value={value?.address_state || ''}
                onChange={(e) => {
                  // Fields are always disabled, but keeping onChange for consistency
                  const stateValue = e.target.value
                    .toUpperCase()
                    .slice(0, field.state_maximum_length || 2)
                  onChange({
                    ...value,
                    address_state: stateValue,
                  })
                }}
                placeholder='CA'
                disabled={true}
              />
            )}

            {/* County */}
            {field.county_field_id !== undefined && (
              <InputField
                width='100%'
                type='text'
                label='County'
                isRequired={field.county_is_required === 'yes'}
                value={value?.county || ''}
                onChange={(e) => {
                  // Fields are always disabled, but keeping onChange for consistency
                  onChange({
                    ...value,
                    county: e.target.value,
                  })
                }}
                placeholder='County'
                disabled={true}
              />
            )}
          </SectionRow>
        )}
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </FieldContainer>
  )
}

export const TableField = ({
  field,
  value,
  onChange,
  error,
  isRequired,
}: {
  field: FormFieldDto
  value: any[]
  onChange: (val: any[]) => void
  error?: string
  isRequired?: boolean
}) => {
  const tableData = value || []

  return (
    <FieldContainer isError={!!error}>
      <Label required={isRequired}>{field.display_label}</Label>
      <div
        style={{
          border: `1px solid ${theme.colors.borderColor}`,
          background: theme.colors.backgroundColorContainer,
          borderRadius: '16px',
          overflowX: 'auto',
          marginTop: '10px',
          padding: '10px',
        }}
      >
        {tableData.map((row, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              background: theme.colors.white,
              border: `1px solid ${theme.colors.borderColor}`,
              padding: '10px',
              borderRadius: '12px',
              position: 'relative',
              marginBottom: '10px',
            }}
          >
            {field.child_fields?.map((childField) => (
              <FieldContainer key={childField.id}>
                {/* {childField.type === 'string_picker_field' ? (
                  <Select
                    options={
                      childField.options?.map((option) => option.key) || []
                    }
                    value={
                      childField.options?.find(
                        (option) => option.value === row[childField.id] || ''
                      )?.key || ''
                    }
                    onChange={(e) => {
                      const newData = [...tableData]
                      newData[rowIndex] = {
                        ...newData[rowIndex],
                        [childField.id]: e.target.value,
                      }
                      onChange(newData)
                    }}
                  />
                ) : ( */}
                <InputField
                  width='100%'
                  type='text'
                  label={childField.display_label}
                  isRequired={childField.required}
                  value={row[childField.id] || ''}
                  onChange={(e) => {
                    const newData = [...tableData]
                    newData[rowIndex] = {
                      ...newData[rowIndex],
                      [childField.id]: e.target.value,
                    }
                    onChange(newData)
                  }}
                  placeholder={childField.display_label}
                />
                {/* )} */}
              </FieldContainer>
            ))}
            <div
              style={{
                position: 'absolute',
                right: '5px',
                top: '5px',
              }}
            >
              <CloseIconButton
                iconType='bold'
                textColor={theme.colors.secondary}
                onClick={() => {
                  const newData = tableData.filter(
                    (_, index) => index !== rowIndex
                  )
                  onChange(newData)
                }}
              />
            </div>
          </div>
        ))}

        {/* Add Button */}
        {(!field.maximum_number || tableData.length < field.maximum_number) && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Button
              variant='contained'
              onClick={() => {
                const newRow = {}
                onChange([...tableData, newRow])
              }}
            >
              {field.btn_display_value || 'Add Item'}
            </Button>
          </div>
        )}
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </FieldContainer>
  )
}
